// src/core/reaction-delta.js — Spec arengas v1.2 §10.
//
// Helpers puros + aplicador DOM para el optimistic update del contador de
// reacciones. Sin esto, el usuario ve el contador stale entre el click y el
// re-render (cache TTL + RPC + fetch + render = ~300-800ms perceptibles).
//
// computeReactionDelta es puro y testeable sin DOM. applyReactionDelta lo
// aplica a un scope (typically appRoot) — el caller revierte llamando con
// los deltas invertidos si el RPC falla.

// computeReactionDelta({ clickedReaction, isActiveOnClicked, prevActiveReaction })
// devuelve los cambios a aplicar al DOM.
//
// clickedReaction:       'escuchar' | 'pego' | 'loco' | 'pasto'
// isActiveOnClicked:     true si el botón clickeado ya tenía data-active='1'
//                        (los 3 positivos sí lo usan; 'pasto' NO)
// prevActiveReaction:    qué reacción positiva tenía el jugador antes — o null
//
// Salida: Array<{ targetReaction, delta, becomesActive }>
//   - targetReaction: clase data-reaction del botón a tocar
//   - delta: +1 o -1 sobre el contador
//   - becomesActive: true | false (cambia data-active) | null (no toca)
//
// Casos:
//   pasto:                acumulativo +1 (no toggle, no afecta otros botones)
//   isActiveOnClicked:    toggle off → −1, becomesActive:false
//   sin previa:           +1, becomesActive:true
//   con previa distinta:  prev −1 (becomesActive:false) + clicked +1 (becomesActive:true)
export const computeReactionDelta = ({ clickedReaction, isActiveOnClicked, prevActiveReaction }) => {
  if (clickedReaction === 'pasto') {
    return [{ targetReaction: 'pasto', delta: 1, becomesActive: null }]
  }
  if (isActiveOnClicked) {
    return [{ targetReaction: clickedReaction, delta: -1, becomesActive: false }]
  }
  if (prevActiveReaction && prevActiveReaction !== clickedReaction) {
    return [
      { targetReaction: prevActiveReaction, delta: -1, becomesActive: false },
      { targetReaction: clickedReaction,    delta: 1,  becomesActive: true },
    ]
  }
  return [{ targetReaction: clickedReaction, delta: 1, becomesActive: true }]
}

// Invierte los deltas (para revertir en caso de error del RPC).
export const invertDeltas = (deltas) => deltas.map(d => ({
  targetReaction: d.targetReaction,
  delta: -d.delta,
  becomesActive: d.becomesActive === null ? null : !d.becomesActive,
}))

// Reconcilia el MODELO en memoria (el objeto de la card/arenga del feed) tras
// una reacción OK, en línea con los deltas que ya se aplicaron al DOM. NO hace
// fetch: el feed real converge solo por su TTL/realtime. Mantiene coherentes el
// conteo del tipo y el estado "mi reacción" para los re-renders que pintan
// DESDE el objeto en memoria (reabrir el modal de detalle, rebuild de la
// muralla): así el contador no "revierte" al valor viejo y el botón queda
// marcado como ya-reaccionado (el usuario no re-clickea para re-sumar).
//
// model:  objeto con campos de conteo escuchar/pego/loco/pasto + miReaccion
//         (escuchar|pego|loco|null) + miAlPasto (bool). Se muta in-place.
// deltas: salida de computeReactionDelta — targetReaction matchea el campo de
//         conteo del objeto 1:1.
// clickedReaction / isActiveOnClicked: el click que originó los deltas.
//
// Devuelve el mismo objeto (mutado) para encadenar/testear. model nulo → no-op.
export const applyReactionToModel = (model, deltas, { clickedReaction, isActiveOnClicked }) => {
  if (!model) return model
  for (const { targetReaction, delta } of deltas) {
    model[targetReaction] = Math.max(0, (model[targetReaction] || 0) + delta)
  }
  if (clickedReaction === 'pasto') {
    // al_pasto es acumulativo + 1 reporte por jugador: queda marcado, no togglea.
    model.miAlPasto = true
  } else {
    // toggle off → sin reacción; alta/switch → la nueva reacción positiva.
    model.miReaccion = isActiveOnClicked ? null : clickedReaction
  }
  return model
}

// Aplica los deltas al DOM. scope = nodo contenedor (typically document o
// appRoot). selector = '.arenga-react' / '.tribuna-audio-react' / '.cabala-
// react' / '.tribuna-cabala-react' / '.promesa-react' / '.tribuna-promesa-
// react' (la clase CSS del botón).
//
// Usa querySelectorAll porque la misma reacción puede aparecer N veces en el
// DOM: el mosaico animado de cábalas duplica cada card 6 veces (2 copias por
// lane × 3 lanes), y en arengas el modal de detalle aparece a la vez que la
// card del listado. Sin esto, solo se actualizaba la primera copia → contador
// desincronizado entre instancias.
//
// Para cada delta encuentra TODOS los botones que matchean [data-id][data-
// reaction], actualiza el textContent del último <span> (donde vive el
// contador) y opcionalmente el atributo data-active. Si no hay matches, el
// delta se ignora.
export const applyReactionDelta = ({ scope, selector, arengaId, deltas }) => {
  for (const { targetReaction, delta, becomesActive } of deltas) {
    const buttons = scope.querySelectorAll(`${selector}[data-id="${arengaId}"][data-reaction="${targetReaction}"]`)
    for (const button of buttons) {
      const spans = button.querySelectorAll('span')
      const counterSpan = spans[spans.length - 1]
      if (counterSpan) {
        const cur = parseInt(counterSpan.textContent || '0', 10) || 0
        counterSpan.textContent = String(Math.max(0, cur + delta))
      }
      if (becomesActive !== null) button.dataset.active = becomesActive ? '1' : '0'
    }
  }
}
